import { PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  all, call, put, select, takeLatest,
} from 'redux-saga/effects'
import { actions as appActions } from '../state'
import { ReturnCall } from '../types'
import apis from './StockAdjustmentInBatchApi'
import { selectAdjustment, selectFilters } from './StockAdjustmentInBatchSeletor'
import { actions } from './StockAdjustmentInBatchState'
import {
  ActionPendingPayloadCancel,
  ActionPendingPayloadConfirm,
  Adjustment,
  AdjustmentStatus,
  ErrorTypes,
} from './StockAdjustmentInBatchTypes'

function* submitAdjustmentFile(data: PayloadAction<File>) {
  yield put(appActions.isLoading(true))
  yield put(actions.resetUploadPage())

  try {
    const result: ReturnCall<typeof apis.postAdjustmentFile> = yield call(
      apis.postAdjustmentFile,
      data.payload,
    )

    if (!result.isOk) {
      toast.error('Falha ao enviar a planilha de ajustes')
      if (result.type === ErrorTypes.INVALID_STRUCTURE) {
        yield put(actions.setInvalidStructure(result.data))
      }

      return
    }

    yield put(actions.setAdjustment(result.data))

    if (result.data.status === AdjustmentStatus.INVALID) {
      toast.error('Falha ao processar. Faça download do arquivo!')
      return
    }

    toast.success('Arquivo enviado com sucesso!')
  } catch (error: any) {
    toast.error(`Falha ao enviar a planilha de ajustes: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* downloadAdjustmentResultFile() {
  yield put(appActions.isLoading(true))
  const adjustment: Adjustment = yield select(selectAdjustment)

  try {
    yield call(apis.getAdjustmentCsvFile, adjustment.id)
  } catch (error: any) {
    toast.error(`Falha ao fazer download do resultado de ajustes: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getAdjustmentDetails(data: PayloadAction<number>) {
  yield put(appActions.isLoading(true))

  try {
    const { data: adjustmentDetails } = yield call(apis.getAdjustmentDetails, data.payload)
    yield put(actions.setAdjustmentDetails(adjustmentDetails))
  } catch (error: any) {
    toast.error(`Falha ao buscar ajustes: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* getPendingAdjustments() {
  yield put(appActions.isLoading(true))
  try {
    const {
      startDate, endDate, user,
    } = yield select(selectFilters)

    const { data } = yield call(
      apis.getPendingAdjustments,
      {
        startDate,
        endDate,
        user,
      },
    )
    yield put(actions.setPendingAdjustment(data))
  } catch {
    toast.error('Falha ao buscar ajustes pendentes!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* confirmPendingAdjustments(data: PayloadAction<ActionPendingPayloadConfirm>) {
  yield put(appActions.isLoading(true))
  const { history, adjustmentId, decision } = data.payload
  try {
    yield call(apis.confirmAdjustmentDetails, adjustmentId, decision)
    toast.success('Ajuste confirmado com sucesso')
    history.push('/adjustments/pending-adjustments')
  } catch (error: any) {
    toast.error(`Falha ao confirmar ajustes pendentes: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

function* cancelPendingAdjustments(data: PayloadAction<ActionPendingPayloadCancel>) {
  yield put(appActions.isLoading(true))
  const { history, adjustmentId } = data.payload
  try {
    yield call(apis.cancelAdjustmentDetails, adjustmentId)
    toast.success('Ajuste cancelado')
    history.push('/adjustments/pending-adjustments')
  } catch (error: any) {
    toast.error(`Falha ao cancelar ajustes pendentes: ${error?.response?.data?.message ?? error.message}`)
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.submitAdjustmentFile.type, submitAdjustmentFile),
    takeLatest(actions.downloadAdjustmentResultFile.type, downloadAdjustmentResultFile),
    takeLatest(actions.getPendingAdjustments.type, getPendingAdjustments),
    takeLatest(actions.getAdjustmentDetails.type, getAdjustmentDetails),
    takeLatest(actions.confirmPendingAdjustments.type, confirmPendingAdjustments),
    takeLatest(actions.cancelPendingAdjustments.type, cancelPendingAdjustments),
  ])
}
